/**
 * Routines (slice 5 spec §11.1; board 80): work the crew does on a schedule,
 * for one person. Mine (the default) and Everyone (`?for=all`), each with
 * its count, and the workspace's timezone; then Routines, Reminders and a folded Done this
 * week. A row's lanes are fixed: title and brief, When over Next, For, the
 * last run, and the On switch. On a phone each row is a card. A row opens
 * Routine detail; New routine opens its window.
 */
import { Button } from "@crew/ui/components/button";
import { PersonChip } from "@crew/ui/components/crew-avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@crew/ui/components/collapsible";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import { Switch } from "@crew/ui/components/switch";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { BackLink } from "@crew/ui/components/back-link";
import { PageHeader } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { ChevronRight, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import { personTint } from "../../app/tints";
import { useMessaging } from "../messaging/api";
import { useRoutines, useToggleRoutine, type RoutineItem, type RoutineTab } from "./api";
import { LastRunMark } from "./parts";
import { AreaFilter } from "../areas/area-controls";
import { useAreaFilter } from "../areas/use-area-filter";
import { filingArea } from "../areas/area-view";
import { AreaChip } from "../areas/parts";
import { NewRoutineWindow } from "./routine-form";
import {
  footerLine,
  isMuted,
  lastRunView,
  type LastRunView,
  nextLabel,
  sections,
  switchState,
  weekLine,
  whenLabel,
} from "./routine-view";

/** Mine first (`?for=me` still opens it, for older links); `?for=all` is Everyone. */
const tabOf = (v: string | null): RoutineTab => (v === "all" ? "everyone" : "mine");

/** "You" for the viewer, else their name. */
function forName(r: RoutineItem, me: Me): string {
  if (!r.for) return "—";
  return r.for.id === me.user.id ? "You" : r.for.name;
}

/** The On switch: the person's own, and the owner's on anyone's (off only). */
function RoutineSwitch({ routine, me }: { routine: RoutineItem; me: Me }) {
  const toggle = useToggleRoutine();
  const state = switchState(routine, { id: me.user.id, role: me.user.role });
  const pending = toggle.isPending ? toggle.variables : null;
  return (
    <Switch
      aria-label={`${routine.title}: on`}
      data-routine-switch={routine.id}
      checked={pending ? pending.enabled : state.checked}
      disabled={state.disabled || toggle.isPending}
      onCheckedChange={(enabled) => toggle.mutate({ id: routine.id, enabled })}
    />
  );
}

/** What a row shows, worked out once for both layouts. */
type RowView = {
  routine: RoutineItem;
  me: Me;
  href: string;
  muted: boolean;
  brief: string;
  when: string;
  next: string;
  last: LastRunView;
};

function rowView(routine: RoutineItem, me: Me, now: Date): RowView {
  const tz = me.workspace.timezone;
  // A reminder asked in a chat says where (board 80); the rest, their first sentence.
  const fromChat = routine.kind === "once" && !routine.owns_thread && routine.thread_title;
  return {
    routine,
    me,
    href: `/routines/${routine.id}`,
    muted: isMuted(routine) || routine.ended_at !== null,
    brief: fromChat ? `From “${routine.thread_title}”` : routine.brief,
    when: whenLabel(routine, tz, now),
    next: nextLabel(routine, me.user.id, tz, now),
    last: lastRunView(routine, me.user.id, tz, now),
  };
}

/** 1440: fixed lanes (title and brief, When over Next, For, the last run, the switch). */
function LaneRow({ v }: { v: RowView }) {
  const { routine, muted } = v;
  return (
    <div className="hidden items-center gap-4 px-5 py-4 md:flex">
      <Link
        href={v.href}
        className="flex min-w-0 flex-1 flex-col gap-0.5 after:absolute after:inset-0"
      >
        <span
          className={cn(
            "truncate text-body font-semibold",
            muted ? "text-hint" : "text-foreground",
          )}
        >
          {routine.title}
          <AreaChip area={routine.area} className="ml-2" />
        </span>
        <span className={cn("truncate text-meta", muted ? "text-hint" : "text-muted-foreground")}>
          {v.brief}
        </span>
      </Link>
      <span className="flex w-42.5 shrink-0 flex-col gap-0.5 text-meta">
        <span className={cn("font-medium", muted ? "text-hint" : "text-copy")}>{v.when}</span>
        <span className="text-hint">{v.next}</span>
      </span>
      <span className="flex w-37.5 shrink-0 items-center">
        {routine.for ? (
          <PersonChip
            name={forName(routine, v.me)}
            initials={routine.for.initials}
            tint={personTint(routine.for.tint)}
            size={22}
            className={cn("min-w-0", muted && "text-hint")}
          />
        ) : null}
      </span>
      <LastRunMark view={v.last} className="w-57.5 shrink-0" />
      <span className="relative z-10 flex w-11 shrink-0 justify-end">
        <RoutineSwitch routine={routine} me={v.me} />
      </span>
    </div>
  );
}

/** 390: a card (the title, "When · For", the last run, the switch on the right). */
function CardRow({ v }: { v: RowView }) {
  const { routine, muted } = v;
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 md:hidden">
      <Link
        href={v.href}
        className="flex min-w-0 flex-1 flex-col gap-1 after:absolute after:inset-0"
      >
        <span className={cn("text-body font-semibold", muted ? "text-hint" : "text-foreground")}>
          {routine.title}
          <AreaChip area={routine.area} className="ml-2" />
        </span>
        <span className={cn("text-meta", muted ? "text-hint" : "text-copy")}>
          {v.when} · {forName(routine, v.me)}
        </span>
        <LastRunMark view={v.last} />
      </Link>
      <span className="relative z-10 pt-0.5">
        <RoutineSwitch routine={routine} me={v.me} />
      </span>
    </div>
  );
}

function Row({ routine, me, now }: { routine: RoutineItem; me: Me; now: Date }) {
  const v = rowView(routine, me, now);
  return (
    <li
      data-routine-id={routine.id}
      data-muted={v.muted || undefined}
      className="relative border-t border-border first:border-t-0"
    >
      <LaneRow v={v} />
      <CardRow v={v} />
    </li>
  );
}

function RowList({ items, me, now }: { items: RoutineItem[]; me: Me; now: Date }) {
  return (
    <ul className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
      {items.map((r) => (
        <Row key={r.id} routine={r} me={me} now={now} />
      ))}
    </ul>
  );
}

function Section({
  title,
  aside,
  children,
  name,
}: {
  title: string;
  aside: ReactNode;
  children: ReactNode;
  name: string;
}) {
  return (
    <section className="flex flex-col gap-3" data-section={name}>
      <div className="flex items-baseline gap-2.5">
        <h2 className="text-question font-semibold text-foreground">{title}</h2>
        <span className="text-meta text-muted-foreground">{aside}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ children, name }: { children: ReactNode; name: string }) {
  return (
    <p
      data-routines-empty={name}
      className="rounded-xl border border-dashed border-border px-6 py-8 text-center text-body text-muted-foreground"
    >
      {children}
    </p>
  );
}

/**
 * Done this week, folded (spec §11.1), on one line with the footer; its
 * reminders open full width below.
 */
function DoneAndFooter({
  items,
  me,
  now,
  footer,
  week,
}: {
  items: RoutineItem[];
  me: Me;
  now: Date;
  footer: string;
  week: string;
}) {
  const [open, setOpen] = useState(false);
  const footers = (
    <>
      <p
        data-routines-footer
        className="hidden text-meta text-muted-foreground md:ml-auto md:block"
      >
        {footer}
      </p>
      <p className="text-meta text-muted-foreground md:hidden">{week}</p>
    </>
  );
  if (items.length === 0) return <div className="flex">{footers}</div>;
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-section="done" className="flex flex-col">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <CollapsibleTrigger
          className="group flex items-center gap-2.5 self-start py-1 text-body font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-done-toggle
        >
          <ChevronRight
            aria-hidden
            className="size-3.5 text-muted-foreground transition-transform group-data-[panel-open]:rotate-90"
          />
          Done this week
          <span className="text-meta font-normal text-muted-foreground">
            {items.length} {items.length === 1 ? "reminder" : "reminders"}
          </span>
        </CollapsibleTrigger>
        {footers}
      </div>
      <CollapsibleContent className="pt-3">
        <RowList items={items} me={me} now={now} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function RoutinesBody({ tab, area, me }: { tab: RoutineTab; area: string | null; me: Me }) {
  const list = useRoutines(tab, area);
  const now = new Date(useNow());
  if (list.error && !list.data) {
    return <p className="text-body text-muted-foreground">{errorHint(list.error)}</p>;
  }
  if (!list.data) {
    return (
      <div className="flex justify-center py-10 text-muted-foreground">
        <Spinner className="size-5" />
      </div>
    );
  }
  const { routines, reminders, done } = sections(list.data.routines);
  const upcoming = routines.length + reminders.length;
  return (
    <>
      {list.isRefetchError ? (
        <p role="alert" className="text-meta text-destructive">
          Couldn't refresh the routines: these may be out of date. {errorHint(list.error)}
        </p>
      ) : null}
      <Section title="Routines" aside={routines.length} name="routines">
        {routines.length > 0 ? (
          <RowList items={routines} me={me} now={now} />
        ) : (
          <Empty name="routines">
            {tab === "mine"
              ? "None of yours yet. Make one with New routine, or ask in any chat."
              : "No routines yet. Make one with New routine, or ask in any chat."}
          </Empty>
        )}
      </Section>
      <Section
        title="Reminders"
        aside={
          <>
            <span className="hidden md:inline">Run once, in the chat they were asked in</span>
            <span className="md:hidden">{reminders.length}</span>
          </>
        }
        name="reminders"
      >
        {reminders.length > 0 ? (
          <RowList items={reminders} me={me} now={now} />
        ) : (
          <Empty name="reminders">
            None coming up. Ask in any chat: “Remind me Thursday at 6:30 to call Miller.”
          </Empty>
        )}
      </Section>
      <DoneAndFooter
        items={done}
        me={me}
        now={now}
        footer={footerLine(upcoming, list.data.week)}
        week={weekLine(list.data.week)}
      />
    </>
  );
}

function TabRow({
  tab,
  area,
  onChange,
  me,
  onNew,
  onArea,
}: {
  tab: RoutineTab;
  area: string | null;
  onChange: (t: RoutineTab) => void;
  me: Me;
  onNew: () => void;
  onArea: (area: string | null) => void;
}) {
  // Both tabs read the same counts; the shown tab's answer has them.
  const data = useRoutines(tab, area).data;
  const counts = data?.counts;
  return (
    <div className="flex items-center justify-between gap-4">
      <Tabs value={tab} onValueChange={(v) => onChange(v as RoutineTab)}>
        <TabsList aria-label="Whose routines">
          <TabsTrigger value="mine" data-tab="mine">
            Mine
            <span className="text-muted-foreground tabular">{counts?.mine ?? ""}</span>
          </TabsTrigger>
          <TabsTrigger value="everyone" data-tab="everyone">
            Everyone
            <span className="text-muted-foreground tabular">{counts?.all ?? ""}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <span className="ml-auto hidden text-meta text-muted-foreground md:inline" data-timezone>
        Times are in {data?.timezone ?? me.workspace.timezone}
      </span>
      {/* Slice 6d: the Area menu on a desktop; a phone's chips sit under the tabs. */}
      <AreaFilter me={me} value={area} onChange={onArea} className="hidden md:block" />
      <Button onClick={onNew} className="md:hidden" data-new-routine-small>
        <Plus />
        New
      </Button>
    </div>
  );
}

export function RoutinesPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params.get("for"));
  const [area, setArea] = useAreaFilter();
  const [creating, setCreating] = useState(false);
  const messaging = useMessaging();
  const phoneLinked = messaging.data?.apps.some((a) => a.linked) ?? false;
  const setTab = (t: RoutineTab) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (t === "mine") next.delete("for");
        else next.set("for", "all");
        return next;
      },
      { replace: true },
    );
  return (
    <main className="flex w-full max-w-6xl flex-col gap-6 px-4 pt-6 pb-16 sm:px-6 md:gap-7 md:px-12 md:pt-9">
      <BackLink href="/settings">Settings</BackLink>
      <PageHeader
        title="Routines"
        description="Work the crew does on a schedule, for one person."
        actions={
          <Button
            onClick={() => setCreating(true)}
            data-new-routine-button
            className="hidden md:inline-flex"
          >
            <Plus />
            New routine
          </Button>
        }
      />
      <TabRow
        tab={tab}
        area={area}
        onChange={setTab}
        me={me}
        onNew={() => setCreating(true)}
        onArea={setArea}
      />
      <AreaFilter me={me} value={area} onChange={setArea} className="-mt-2 md:hidden" />
      <RoutinesBody tab={tab} area={area} me={me} />
      <NewRoutineWindow
        open={creating}
        onOpenChange={setCreating}
        me={me}
        phoneLinked={phoneLinked}
        areaId={filingArea(area, me)}
      />
    </main>
  );
}
